import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  addChatMedia,
  createCommunityComment,
  deleteChatMedia,
  deleteCommunityComment,
  fetchChatMedia,
  fetchCommunity,
  fetchCommunityChatUsers,
  fetchCommunityComment,
  fetchCommunityComments,
  fetchCommunityReplyMentionComments,
  fetchCommunityStripeURL,
  fetchCommunityUserNum,
  fetchFeaturedCommunityComments,
  helpfulComment,
  helpfulCommunityComment,
  lastCommunityChatVisit,
  reportComment,
  reportCommunityComment,
  updateCommunityComment,
} from '../endpoints/api';
import {User} from './useAuth';
import {tail} from 'lodash';
import {ToastMessage} from '../components/toast';
import {AxiosError} from 'axios';

const INTERVAL_MS = 5000; // 5 seconds

export const useInfiniteFeaturedCommunityComments = (
  communityId: number,
  enabled: boolean = true
) => {
  return useInfiniteQuery(
    ['infinite_featured_community_comments', communityId],
    ({pageParam = 1}) => fetchFeaturedCommunityComments({communityId, page: pageParam}),
    {
      getNextPageParam: (lastPage) => {
        const currentPage = lastPage.data.meta?.current_page || lastPage.data.current_page;
        const lastPageNum = lastPage.data.meta?.last_page || lastPage.data.last_page;
        if (currentPage !== lastPageNum) {
          return currentPage + 1;
        }
      },
      refetchInterval: INTERVAL_MS,
      enabled: enabled && typeof communityId === 'number',
    }
  );
};

export const useInfiniteCommunityComments = (communityId: number, enabled: boolean = true) => {
  return useInfiniteQuery(
    ['infinite_community_comments', communityId],
    ({pageParam = 1}) => fetchCommunityComments({communityId, page: pageParam}),
    {
      getNextPageParam: (lastPage) => {
        const currentPage = lastPage.data.meta?.current_page || lastPage.data.current_page;
        const lastPageNum = lastPage.data.meta?.last_page || lastPage.data.last_page;
        if (currentPage !== lastPageNum) {
          return currentPage + 1;
        }
      },
      refetchInterval: INTERVAL_MS,
      enabled: enabled && typeof communityId === 'number',
    }
  );
};

export const useCreateCommunityComment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      communityId,
      content,
      parent_id,
      is_featured,
      user,
      hasImage = false
    }: {
      communityId: number;
      content: string;
      parent_id?: string;
      is_featured?: boolean;
      user?: User;
      hasImage?: boolean;
    }) => createCommunityComment({communityId, content, parent_id, is_featured}),
    {
      onMutate: async (newComment) => {
        // Cancel any outgoing refetches
        // (so they don't overwrite our optimistic update)
        await queryClient.cancelQueries({
          queryKey: ['infinite_community_comments', newComment.communityId],
        });

        // Snapshot the previous value
        const previousComments: any = queryClient.getQueryData([
          'infinite_community_comments',
          newComment.communityId,
        ]);

        if (previousComments && previousComments.pages[0]) {
          // If this is a reply, find the parent comment to attach
          let parentComment = null;
          if (newComment.parent_id) {
            for (const page of previousComments.pages) {
              parentComment = page.data.data.find((c: any) => c.id === newComment.parent_id);
              if (parentComment) break;
            }
          }

          const newData = {
            ...previousComments,
            pages: [
              {
                ...previousComments.pages[0],
                data: {
                  ...previousComments.pages[0].data,
                  data: [
                    {
                      hasImage: true,
                      id: '1',
                      ...newComment,
                      user_id: newComment.user?.id,
                      parent: parentComment,
                    },
                    ...previousComments.pages[0].data.data,
                  ],
                },
              },
              ...tail(previousComments.pages),
            ],
          };
          // Optimistically update to the new value
          queryClient.setQueryData(
            ['infinite_community_comments', newComment.communityId],
            newData
          );
        }

        // Return a context with the previous and new comment
        return {previousComments, newComment};
      },
      onSettled: (newComment, error, variables, context) => {
        if (error && error instanceof AxiosError) {
          ToastMessage({
            status: 'error',
            text: error.message,
          });
        }
        //queryClient.invalidateQueries(['infinite_community_comments', variables.communityId]);
        //queryClient.invalidateQueries(['community_comments', variables.communityId]);
      },
    }
  );
};

export const useUpdateCommunityComment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({communityId, commentId, content}: {communityId: number; commentId: string; content: string}) =>
      updateCommunityComment({communityId, commentId, content}),
    {
      onMutate: async (updatedComment) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries({
          queryKey: ['infinite_community_comments', updatedComment.communityId],
        });
        await queryClient.cancelQueries({
          queryKey: ['community_comments', updatedComment.communityId],
        });

        // Snapshot the previous value
        const previousComments: any = queryClient.getQueryData([
          'infinite_community_comments',
          updatedComment.communityId,
        ]);
        const previousRepliesMentions: any = queryClient.getQueryData([
          'community_comments',
          updatedComment.communityId,
        ]);

        if (previousComments && previousComments.pages) {
          // Update the comment and any children that reference it as parent
          const newData = {
            ...previousComments,
            pages: previousComments.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.map((comment: any) => {
                  // Update the comment itself
                  if (comment.id === updatedComment.commentId) {
                    return {...comment, content: updatedComment.content};
                  }
                  // Update parent reference in child comments
                  if (comment.parent && comment.parent.id === updatedComment.commentId) {
                    return {
                      ...comment,
                      parent: {...comment.parent, content: updatedComment.content},
                    };
                  }
                  return comment;
                }),
              },
            })),
          };

          queryClient.setQueryData(['infinite_community_comments', updatedComment.communityId], newData);
        }

        // Also update replies and mentions cache
        if (previousRepliesMentions?.data) {
          const updateCommentInArray = (comments: any[]) => 
            comments.map((comment: any) => {
              if (comment.id === updatedComment.commentId) {
                return {...comment, content: updatedComment.content};
              }
              if (comment.parent && comment.parent.id === updatedComment.commentId) {
                return {
                  ...comment,
                  parent: {...comment.parent, content: updatedComment.content},
                };
              }
              return comment;
            });

          const newRepliesMentions = {
            ...previousRepliesMentions,
            data: {
              ...previousRepliesMentions.data,
              replies: updateCommentInArray(previousRepliesMentions.data.replies || []),
              mentions: updateCommentInArray(previousRepliesMentions.data.mentions || []),
            },
          };

          queryClient.setQueryData(['community_comments', updatedComment.communityId], newRepliesMentions);
        }

        return {previousComments, previousRepliesMentions};
      },
      onSettled: (newComment, error, variables, context) => {
        if (error && error instanceof AxiosError) {
          ToastMessage({
            status: 'error',
            text: error.message,
          });
        }
        queryClient.invalidateQueries(['infinite_community_comments', variables.communityId]);
        queryClient.invalidateQueries(['community_comments', variables.communityId]);
      },
    }
  );
};

export const useDeleteCommunityComment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({communityId, commentId}: {communityId: number; commentId: string}) =>
      deleteCommunityComment({communityId, commentId}),
    {
      onMutate: async (variables) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries(['infinite_community_comments', variables.communityId]);
        await queryClient.cancelQueries(['community_comments', variables.communityId]);

        // Snapshot the previous values
        const previousInfiniteComments = queryClient.getQueryData(['infinite_community_comments', variables.communityId]);
        const previousComments = queryClient.getQueryData(['community_comments', variables.communityId]);

        // Optimistically mark the comment as deleted in infinite query
        queryClient.setQueryData(['infinite_community_comments', variables.communityId], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.map((comment: any) => 
                  comment.id === variables.commentId 
                    ? {...comment, content: 'Message deleted by author', status: 0} 
                    : comment
                ),
              },
            })),
          };
        });

        // Optimistically mark the comment as deleted in regular query
        queryClient.setQueryData(['community_comments', variables.communityId], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.map((comment: any) => 
                  comment.id === variables.commentId 
                    ? {...comment, content: 'Message deleted by author', status: 0} 
                    : comment
                ),
              },
            })),
          };
        });

        return {previousInfiniteComments, previousComments};
      },
      onError: (error, variables, context: any) => {
        // Rollback on error
        if (context?.previousInfiniteComments) {
          queryClient.setQueryData(['infinite_community_comments', variables.communityId], context.previousInfiniteComments);
        }
        if (context?.previousComments) {
          queryClient.setQueryData(['community_comments', variables.communityId], context.previousComments);
        }
        if (error && error instanceof AxiosError) {
          ToastMessage({
            status: 'error',
            text: error.message,
          });
        }
      },
      onSettled: (newComment, error, variables, context) => {
        queryClient.invalidateQueries(['infinite_community_comments', variables.communityId]);
        queryClient.invalidateQueries(['community_comments', variables.communityId]);
      },
    }
  );
};

export const useReportCommunityComment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({communityId, commentId}: {communityId: number; commentId: string}) =>
      reportCommunityComment({communityId, commentId}),
    {
      onMutate: async (variables) => {
        // Cancel any outgoing refetches
        await queryClient.cancelQueries(['infinite_community_comments', variables.communityId]);
        await queryClient.cancelQueries(['community_comments', variables.communityId]);
        await queryClient.cancelQueries(['infinite_featured_community_comments', variables.communityId]);

        // Snapshot the previous values
        const previousInfiniteComments = queryClient.getQueryData(['infinite_community_comments', variables.communityId]);
        const previousComments = queryClient.getQueryData(['community_comments', variables.communityId]);
        const previousFeaturedComments = queryClient.getQueryData(['infinite_featured_community_comments', variables.communityId]);

        // Optimistically remove the comment from infinite query
        queryClient.setQueryData(['infinite_community_comments', variables.communityId], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.filter((comment: any) => comment.id !== variables.commentId),
              },
            })),
          };
        });

        // Optimistically remove from replies and mentions
        queryClient.setQueryData(['community_comments', variables.communityId], (old: any) => {
          if (!old?.data) return old;
          return {
            ...old,
            data: {
              ...old.data,
              replies: old.data.replies?.filter((comment: any) => comment.id !== variables.commentId) || [],
              mentions: old.data.mentions?.filter((comment: any) => comment.id !== variables.commentId) || [],
            },
          };
        });

        // Optimistically remove from featured comments
        queryClient.setQueryData(['infinite_featured_community_comments', variables.communityId], (old: any) => {
          if (!old) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              data: {
                ...page.data,
                data: page.data.data.filter((comment: any) => comment.id !== variables.commentId),
              },
            })),
          };
        });

        return {previousInfiniteComments, previousComments, previousFeaturedComments};
      },
      onError: (error, variables, context: any) => {
        // Rollback on error
        if (context?.previousInfiniteComments) {
          queryClient.setQueryData(['infinite_community_comments', variables.communityId], context.previousInfiniteComments);
        }
        if (context?.previousComments) {
          queryClient.setQueryData(['community_comments', variables.communityId], context.previousComments);
        }
        if (context?.previousFeaturedComments) {
          queryClient.setQueryData(['infinite_featured_community_comments', variables.communityId], context.previousFeaturedComments);
        }
        if (error && error instanceof AxiosError) {
          ToastMessage({
            status: 'error',
            text: error.message,
          });
        }
      },
      onSettled: (newComment, error, variables, context) => {
        queryClient.invalidateQueries(['infinite_community_comments', variables.communityId]);
        queryClient.invalidateQueries(['community_comments', variables.communityId]);
        queryClient.invalidateQueries(['infinite_featured_community_comments', variables.communityId]);
      },
    }
  );
};

export const useHelpfulCommunityComment = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({communityId, commentId}: {communityId: number; commentId: string}) =>
      helpfulCommunityComment({communityId, commentId}),
    {
      onSettled: (newComment, error, variables, context) => {
        queryClient.invalidateQueries(['infinite_community_comments', variables.communityId]);
        queryClient.invalidateQueries(['community_comments', variables.communityId]);
      },
    }
  );
};

export const useFetchCommunityChatUsers = (communityId: number) => {
  return useInfiniteQuery(
    ['infinite_community_users', communityId],
    ({pageParam = 1}) => fetchCommunityChatUsers({communityId, page: pageParam}),
    {
      getNextPageParam: (lastPage) => {
        if (lastPage.data.current_page !== lastPage.data.last_page) {
          return lastPage.data.current_page + 1;
        }
      },
      enabled: typeof communityId === 'number',
    }
  );
};

export function useFetchRepliesAndMentions(communityId: number, enabled: boolean = true) {
  return useQuery(
    ['community_replies', communityId],
    () => fetchCommunityReplyMentionComments({communityId}),
    {
      enabled: enabled && typeof communityId === 'number',
      // Refetch the data every INTERVAL_MS
      refetchInterval: INTERVAL_MS,
    }
  );
}

export function useFetchCommunity(communityId: number, enabled: boolean = true) {
  return useQuery(['communities', communityId], () => fetchCommunity(communityId), {
    enabled: enabled && typeof communityId === 'number',
  });
}

export function useFetchCommunityUserNum(communityId: number) {
  return useQuery(['communities_num', communityId], () => fetchCommunityUserNum(communityId), {
    enabled: typeof communityId === 'number',
  });
}

export function useFetchCommunityComment({communityId, commentId, enabled}) {
  return useQuery(
    ['community_comment', communityId, commentId],
    () => fetchCommunityComment({communityId, commentId}),
    {
      enabled: enabled && typeof communityId === 'number',
      // Refetch the data every INTERVAL_MS
      refetchInterval: INTERVAL_MS,
      retry: 1,
    }
  );
}

export function useFetchCommunityStripeURL(communityId: number, enabled: boolean = true) {
  return useQuery(['communities_stripe', communityId], () => fetchCommunityStripeURL(communityId), {
    enabled: enabled && typeof communityId === 'number',
  });
}

export const useLastChatVisit = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({communityId}: {communityId: number}) => lastCommunityChatVisit({communityId}),
    {
      onSettled: (newComment, error, variables, context) => {
        queryClient.invalidateQueries(['new_community_messages']);
      },
    }
  );
};

export const useAddCommentImage = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({
      formData,
      target_id,
      comment_id,
    }: {
      formData: FormData;
      target_id: string;
      comment_id: string;
    }) =>
      addChatMedia({
        formData,
        target_type: 'community',
        target_id,
        comment_type: 'community_comment',
        comment_id,
      }),
    {
      onSettled: (newComment, error, variables, context) => {
        if (error && error instanceof AxiosError) {
          ToastMessage({
            status: 'error',
            text: error.message,
          });
        }
        queryClient.invalidateQueries(['infinite_community_comments', variables.target_id]);
        queryClient.invalidateQueries(['community_comments', variables.target_id]);
      },
    }
  );
};

export const useDeleteCommentImage = () => {
  const queryClient = useQueryClient();
  return useMutation(({mediaId}: {mediaId: string}) => deleteChatMedia({mediaId}), {
    onSettled: (newComment, error, variables, context) => {
      if (error && error instanceof AxiosError) {
        ToastMessage({
          status: 'error',
          text: error.message,
        });
      }
      queryClient.invalidateQueries(['infinite_community_comments']);
      queryClient.invalidateQueries(['community_comments']);
    },
  });
};

export const useFetchCommentMedia = ({mediaId}) => {
  return useQuery(['community_comment_media', mediaId], () => fetchChatMedia({mediaId}), {
    enabled: typeof mediaId === 'number',
  });
};
